/**
 * ============================================================================
 * ReservedRefundsModal - SHOWS BOOKINGS WITH PENDING REFUNDS
 * ============================================================================
 *
 * Displays bookings that have pending refund requests, reserving funds from
 * the vendor's available balance until the refunds are processed.
 *
 * ============================================================================
 */

import React from 'react';
import { FileText, X, AlertCircle } from 'lucide-react';
import { useReservedRefunds } from '../../../hooks/usePayoutBookings';
import { formatCurrency, formatDate } from '../../../utils/revenueFormatters';
import Modal from '../../ui/Modal';

interface ReservedRefundsModalProps {
  venueId: number;
  onClose: () => void;
  isOpen?: boolean;
}

/**
 * Modal showing bookings with pending refund requests
 */
export function ReservedRefundsModal({ venueId, onClose, isOpen = true }: ReservedRefundsModalProps) {
  // Fetch reserved refunds data using custom hook with authentication
  const { data, isPending, error } = useReservedRefunds(venueId);

  const refunds = data?.data || [];
  const totalReserved = data?.total_reserved || 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" animate={false}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Reserved for Refunds</h3>
            {refunds.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                {refunds.length} pending refund{refunds.length !== 1 ? 's' : ''} • Total: {formatCurrency(totalReserved)}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {isPending ? (
            // Loading state
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
              <p className="text-gray-600">Loading refunds...</p>
            </div>
          ) : error ? (
            // Error state
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-red-900 font-semibold mb-2">Failed to load refunds</p>
              <p className="text-red-700 text-sm">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
            </div>
          ) : refunds.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-900 font-semibold mb-2">No Pending Refunds</p>
              <p className="text-gray-600 text-sm">
                You don't have any pending refund requests at the moment.
              </p>
            </div>
          ) : (
            // Data table
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                      Booking
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                      Refund Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                      Reserved Amount
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {refunds.map((refund) => (
                    <tr key={refund.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">
                        <p className="font-mono text-sm font-semibold text-gray-900">
                          {refund.booking_reference}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Ref #{refund.refund_request_id}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-gray-900">{refund.customer_name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-gray-900">
                          {refund.booking_date ? formatDate(refund.booking_date) : 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Requested {formatDate(refund.created_at)}
                        </p>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(refund.requested_amount)}
                        </p>
                        <p className="text-xs text-gray-500">To customer</p>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <p className="text-sm font-bold text-orange-600">
                          {formatCurrency(refund.reserved_amount)}
                        </p>
                        <p className="text-xs text-gray-500">Your share</p>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                          refund.refund_status === 'processed'
                            ? 'bg-green-100 text-green-800'
                            : refund.refund_status === 'processing'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {refund.refund_status.charAt(0).toUpperCase() + refund.refund_status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-right font-bold text-gray-900">
                      Total Reserved:
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="text-lg font-bold text-orange-600">
                        {formatCurrency(totalReserved)}
                      </p>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Info Message */}
          {refunds.length > 0 && (
            <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-900">About Reserved Funds</p>
                <p className="text-xs text-orange-700 mt-1">
                  These funds are temporarily reserved from your balance to cover pending refunds.
                  Once the refund is processed and the customer receives their money, these reserved funds will be cleared from your account.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
          >
            Close
          </button>
        </div>
    </Modal>
  );
}
